import produce from 'immer';

import {
  NON_PERSISTED_GQL_FIELDS_BY_CHART_TYPE,
  NON_PERSISTED_GQL_FIELDS_FOR_CHART_VIEW_BY_CHART_TYPE,
} from 'analysis-tool/charting/AnalysisChartingConstants';
import {
  AnalysisChartVisualizationConfig,
  AnalysisChartViewConfig,
} from 'analysis-tool/charting/AnalysisChartingTypes';
import {PARAMETERS_TYPENAME_TO_VISUALIZATION_TYPE} from 'insights/analytics-visualizations/AnalyticsVisualizationConstants';
import {
  VisualizationConfig,
  VisualizationType,
} from 'insights/universal-blocks/SharedUniversalBlockViewTypes';

/**
 * Transform a visualization config from the gql shape to match the visualization step config shape
 */
export default function getPersistedVisualizationConfig(
  visualizationConfig: VisualizationConfig
): AnalysisChartVisualizationConfig {
  const {__typename, ...remainingParameters} = visualizationConfig.parameters;
  const visualizationType = PARAMETERS_TYPENAME_TO_VISUALIZATION_TYPE[__typename];

  const parameters = produce(remainingParameters, (draftParameters) => {
    for (const field of NON_PERSISTED_GQL_FIELDS_BY_CHART_TYPE[visualizationType]) {
      if (draftParameters.hasOwnProperty(field)) {
        // @ts-expect-error
        delete draftParameters[field];
      }
    }
  });

  return {
    type: visualizationType,
    parameters,
  } as AnalysisChartVisualizationConfig;
}

const ANALYSIS_CHART_VIEW_EXCLUSIVE_PARAMETERS_TO_DEFAULTS = {
  tryConvertStrings: null,
  fillNullsWithDefaultValues: null,
};

const CHART_TYPES_WITH_LEGEND_SUPPORT = [
  VisualizationType.BAR_CHART,
  VisualizationType.HEATMAP,
  VisualizationType.LINE_CHART,
  VisualizationType.PIE_CHART,
];

/**
 * Transform a visualization config from the gql shape to match the chart view step config shape
 */
export function getPersistedChartViewConfig(
  visualizationConfig: VisualizationConfig
): AnalysisChartViewConfig {
  const {__typename, ...remainingParameters} = visualizationConfig.parameters;
  const visualizationType = PARAMETERS_TYPENAME_TO_VISUALIZATION_TYPE[__typename];

  const parameters = produce(remainingParameters, (draftParameters) => {
    for (const field of NON_PERSISTED_GQL_FIELDS_FOR_CHART_VIEW_BY_CHART_TYPE[visualizationType]) {
      if (draftParameters.hasOwnProperty(field)) {
        // @ts-expect-error
        delete draftParameters[field];
      }
    }
    Object.assign(draftParameters, ANALYSIS_CHART_VIEW_EXCLUSIVE_PARAMETERS_TO_DEFAULTS);
    if (CHART_TYPES_WITH_LEGEND_SUPPORT.includes(visualizationType)) {
      Object.assign(draftParameters, {
        showLegend: draftParameters.showLegend ?? null,
        // @ts-expect-error
        legendLabel: draftParameters.legendLabel ?? null,
        // @ts-expect-error
        legendLocation: draftParameters.legendLocation ?? null,
      });
    }
    if (visualizationType === VisualizationType.SCATTER_PLOT) {
      Object.assign(draftParameters, {
        shapeSeriesColumnName: null,
        seriesColorConfigs: null,
        seriesShapeConfigs: null,
        trendlineType: null,
        annotationLineConfigs: null,
      });
    }
  });

  return {
    type: visualizationType,
    parameters,
  } as AnalysisChartViewConfig;
}
